import { DEVICES } from '../../constants';
import { ICustomElementViewModel, inject, bindable } from 'aurelia';
import { websiteShortCode, environment } from '../../../environment';
import { AuthenticationHandler, Helper } from '../../extensions';
import { SessionService } from '../../../services/session-service';
import { CustomerService } from '../../../services/customer-service';
import './navbar.scss';
import { Notifications } from '../../types/notifications';

@inject(AuthenticationHandler, SessionService, Helper, CustomerService)
export class Navbar implements ICustomElementViewModel {
    @bindable shortcode: string = websiteShortCode() ?? 'CG';
    @bindable user;
    environmentLink: string;
    environment = environment();
    notificationData: Notifications[];
    unreadNotificationsCount: number;
    navbarLogo: string;
    devices = DEVICES;
    width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    canAccessAdminPanel: boolean;
    cartLength: number = 0;
    signInIconVariant: 'filled' | 'outlined' = 'outlined';
    pageSize = 30;
    language;
    private mainScrollElement: HTMLElement | null = null;
    private scrollSyncRetryTimer: number | null = null;

    constructor(private authHandler: AuthenticationHandler, private sessionService: SessionService, private helper: Helper, private customerService: CustomerService) {
        this.environmentLink = this.helper.environmentLink;
        this.navbarLogo = this.helper.dynamicLogo;
    }

    binding() {
        this.loadUserAndData();
    }

    private async loadUserAndData() {
        this.user = await this.sessionService.getProfile();

        if (!this.user) return;

        this.language = await this.sessionService.getLanguage();
        await this.getUnreadNotifications();
        await this.getLatestNotifications();

        const cartService = await this.sessionService.getCartCount();
        this.cartLength = cartService;
    }

    async attached() {
        this.checkCanAccessAdminPanel();
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('scroll', this.handleMainScroll, { passive: true });
        this.setupNavigationBarScrollSync();
    }

    detached() {
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('scroll', this.handleMainScroll);
        this.mainScrollElement?.removeEventListener('scroll', this.handleMainScroll);
        if (this.scrollSyncRetryTimer) {
            window.clearTimeout(this.scrollSyncRetryTimer);
            this.scrollSyncRetryTimer = null;
        }
    }

    private setupNavigationBarScrollSync() {
        const nextScrollElement = document.querySelector('#main-page-host .simplebar-content-wrapper') as HTMLElement | null;

        if (nextScrollElement && this.mainScrollElement !== nextScrollElement) {
            this.mainScrollElement?.removeEventListener('scroll', this.handleMainScroll);
            this.mainScrollElement = nextScrollElement;
            this.mainScrollElement.addEventListener('scroll', this.handleMainScroll, { passive: true });
        }

        if (!this.mainScrollElement) {
            if (!this.scrollSyncRetryTimer) {
                this.scrollSyncRetryTimer = window.setTimeout(() => {
                    this.scrollSyncRetryTimer = null;
                    this.setupNavigationBarScrollSync();
                }, 200);
            }
            return;
        }

        this.handleMainScroll();
    }

    private handleMainScroll = () => {
        const navigationBar = document.querySelector('cds-navigation-bar') as (HTMLElement & { _isAtTop?: boolean; requestUpdate?: () => void }) | null;
        if (!navigationBar) {
            return;
        }

        const scrollTop = this.mainScrollElement ? this.mainScrollElement.scrollTop : window.scrollY;
        const isAtTop = scrollTop === 0;

        if (typeof navigationBar._isAtTop === 'boolean') {
            navigationBar._isAtTop = isAtTop;
            navigationBar.requestUpdate?.();
        }

        const navigation = navigationBar.shadowRoot?.querySelector('.cds-navigation');
        if (!navigation) {
            return;
        }

        navigation.classList.toggle('show-border', !isAtTop);
    };

    handleResize = () => {
        this.width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    };

    redirectToSignIn() {
        void this.authHandler.redirectToSSO({ autologin: true });
    }

    onHoverIcon(bool: boolean = false) {
        return this.signInIconVariant = bool ? 'filled' : 'outlined';
    }

    async handleOptionSelected(event: CustomEvent) {
        const adminUrls: Record<string, string> = {
            'dev': 'https://dev-admin.chicksgroup.com',
            'staging': 'https://staging-admin.chicksgroup.com',
            'production': 'https://admin.chicksgroup.com',
        };

        const routes: Record<string, string> = {
            'Profile': '/customer-portal/profile',
            'Subscription': '/customer-portal/subscription',
            'Orders': '/customer-portal/orders',
            'Balance': '/customer-portal/balance',
            'Verification': '/customer-portal/verification',
            'Mobile Verification': '/customer-portal/verification',
            'Email Verification': '/customer-portal/verification',
            'ID Verification': '/customer-portal/verification',
            'Address Verification': '/customer-portal/verification',
            'Purchased': '/customer-portal/purchased',
            'Swap': '/customer-portal/swap',
            'Sold': '/customer-portal/sold',
            'Security': '/customer-portal/security',
            'Reset Password': '/customer-portal/security/reset-password',
            '2FA Settings': '/customer-portal/security/2fa',
            'Withdraw Password': '/customer-portal/security/withdraw',
            'Support': '/customer-portal/support',
            'Admin Panel': adminUrls[this.environment ?? 'production']
        };

        const selectedOption = event.detail;

        if (selectedOption === 'Sign Out') {
            await this.sessionService.logout();
            return window.location.href = `${this.environmentLink}/?signOutFromBlogs=true`;
        }

        const path = routes[selectedOption];
        if (path) {
            return window.location.href = selectedOption === 'Admin Panel' ? path : `${this.environmentLink}${path}`;
        }

        return null;
    }

    async getUnreadNotifications() {
        this.unreadNotificationsCount = await this.customerService.getAllUnreadCount(this.user.id) as number;
    }

    async getLatestNotifications() {
        if (!this.user) {
            return;
        } else {
            this.pageSize = this.unreadNotificationsCount;
            this.notificationData = await this.customerService.getLatestNotifications(
                this.user.id,
                1,
                this.pageSize,
                false,
                this.language.languageId ?? 1
            ) as Notifications[];
            return this.notificationData;
        }
    }

    async handleCallbackEvent(event) {
        const notificationsId = event.detail.selected[0].id;

        const actions = {
            unread: () => {
                // Handle unread notifications
                this.customerService.markAsRead(notificationsId, false);
            },
            read: () => {
                // Handle read notifications
                this.customerService.markAsRead(notificationsId, true);
            },
            delete: () => {
                // Handle delete notifications
                this.customerService.delete(notificationsId);
            }
        };

        await actions[event.detail.type]?.();
        setTimeout(async () => {
            await this.getLatestNotifications();
            await this.getUnreadNotifications();
        }, 500);
    }

    checkCanAccessAdminPanel(): boolean {
        this.canAccessAdminPanel = this.sessionService.checkRolesForPanelAccess(this.user);
        return this.canAccessAdminPanel;
    }

    viewAsAdmin(isAdmin: boolean) {
        return this.sessionService.saveAdminView(isAdmin);
    }

    setCurrencyMobile(currencyEvent: CustomEvent) {
        this.sessionService.saveCurrency(currencyEvent.detail);
    }

    setLanguageMobile(languageEvent: CustomEvent) {
        this.sessionService.saveLanguage(languageEvent.detail.name);
    }
}
